import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Divider,
  FormControlLabel,
  FormGroup,
  Grid,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import Spinner from '@material-ui/core/CircularProgress';
import PreviewIcon from '../../assets/img/preview.svg';

import useStyles from './styles';
import CustomButton from '../CustomButton';
import { selectBuilderSettings } from '../../redux/selectors/builders';
import { builderSettingsRequest } from '../../redux/actions/builders';
import { BuilderMessaging } from '../../redux/types/builders';
import { replacePlaceholders } from '../../utils/helpers';
import CommunitySearch from '../../containers/CommunitySearch';
import PropertySearch from '../../containers/PropertySearch';
import { generateScheduledTourDateTime } from '../../utils/date';
import appConfig from '../../config';
import { usePermissions } from '../../hooks/usePermissions';

type Variables = {
  visitor_first_name: string;
  scheduled_tour_date: string;
  scheduled_tour_time: string;
  community_name: string;
  community_sales_phone_number: string;
  community_url: string;
  property_address: string;
  property_subdivision: string;
  property_marketing_name: string;
  property_city: string;
  property_state: string;
  property_lot_number: string;
  company_name: string;
  company_marketing_name: string;
  company_legal_name: string;
  property_link: string;
};

type Props = {
  builderId: number;
  builderName: string;
  isLoading: boolean;
  saveMessaging: (messages: BuilderMessaging) => void;
  loadCommunityProperties: (communityId: number) => void;
};

const intervalOptions = ['1 min', '2 min', '5 min', '10 min', '20 min', '30 min', '60 min'];

const Messaging: React.FC<Props> = (props) => {
  const {
    builderId,
    builderName,
    isLoading,
    saveMessaging,
    loadCommunityProperties,
  } = props;

  const builderSettings = useSelector(selectBuilderSettings);
  const [isInitialized, setIsInitialized] = useState(false);
  const [selectedCommunityId, setSelectedCommunityId] = useState<number | null>(null);
  const CHAR_LIMIT = 240;

  const groupHeaders = {
    'Visitor Variables': ['visitor_first_name'],
    'Scheduled Tour Variables': ['scheduled_tour_date', 'scheduled_tour_time'],
    'Community Variables': [
      'community_name',
      'community_sales_phone_number',
      'community_url',
    ],
    'Property Variables': [
      'property_address',
      'property_subdivision',
      'property_marketing_name',
      'property_city',
      'property_state',
      'property_lot_number',
      'property_link'
    ],
    'Company Variables': [
      'company_name',
      'company_marketing_name',
      'company_legal_name',
    ],
  };

  const { scheduledTourDate, scheduledTourTime } = generateScheduledTourDateTime();

  const initialVariables: Variables = {
    visitor_first_name: 'John',
    scheduled_tour_date: scheduledTourDate,
    scheduled_tour_time: scheduledTourTime,
    community_name: '',
    community_sales_phone_number: '',
    community_url: '',
    property_address: '',
    property_subdivision: '',
    property_marketing_name: '',
    property_city: '',
    property_state: '',
    property_lot_number: '',
    company_name: builderName,
    company_marketing_name: builderSettings?.marketing_name ?? '',
    company_legal_name: builderSettings?.legal_name ?? '',
    property_link: '{property_link}',
  };
  const defaultDuringTourMessage = `Hi ${'{visitor_first_name}'}. Welcome to ${builderName}'s ${'{community_name}'}. Enjoy your tour!`;
  const defaultAfterTourMessage = `Thank you for visiting ${builderName}'s home in {community_name}. We hope that you enjoyed your tour. If you have any questions or would like more information, please contact us at {community_sales_phone_number}`;
  const defaultScheduleTourMessage = `Hi ${'{visitor_first_name}'}! Your self-guided tour is confirmed for ${'{scheduled_tour_date}'} at ${'{scheduled_tour_time}'}. We have sent an email with details, but you can simply click this link to access the property and start your tour when you arrive: ${'{property_link}'}`;

  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const [filteredVariables, setFilteredVariables] = useState<string[]>([]);
  const [variables, setVariables] = useState<Variables>(initialVariables);
  const [duringTourMessage, setDuringTourMessage] = useState(
    defaultDuringTourMessage,
  );
  const [afterTourMessage, setAfterTourMessage] = useState(
    defaultAfterTourMessage,
  );
  const [scheduledTourMessage, setScheduledTourMessage] = useState(
    defaultScheduleTourMessage,
  );
  const [duringTourPreview, setDuringTourPreview] = useState(
    replacePlaceholders(defaultDuringTourMessage, variables),
  );
  const [afterTourPreview, setAfterTourPreview] = useState(
    replacePlaceholders(defaultAfterTourMessage, variables),
  );
  const [scheduledTourPreview, setScheduledTourPreview] = useState(
    replacePlaceholders(defaultScheduleTourMessage, variables),
  );
  const [isDuringTourMessageActive, setIsDuringTourMessageActive] = useState(
    false,
  );
  const [isAfterTourMessageActive, setIsAfterTourMessageActive] = useState(
    false,
  );
  const [isScheduledTourMessageActive, setIsScheduledTourMessageActive] = useState(false);
  const [duringTourMessageInterval, setDuringTourMessageInterval] = useState<string | null>(
    builderSettings?.during_tour_message_interval ?? null,
  );
  const [afterTourMessageInterval, setAfterTourMessageInterval] = useState<string | null>(
    builderSettings?.after_tour_message_interval ?? null,
  );
  const [duringTourMessageTime, setDuringTourMessageTime] = useState<string>('');
  const [afterTourMessageTime, setAfterTourMessageTime] = useState<string>('');

  const dispatch = useDispatch();
  const classes = useStyles();
  const afterTourInputRef = useRef(null);
  const duringTourInputRef = useRef(null);
  const scheduledTourInputRef = useRef(null);

  const theme = useTheme();
  const isLg = useMediaQuery(theme.breakpoints.down(1100));

  const { canEdit } = usePermissions();

  useEffect(() => {
    dispatch(builderSettingsRequest(builderId));
  }, [dispatch, builderId]);

  useEffect(() => {
    setIsInitialized(false);

    setIsDuringTourMessageActive(false);
    setDuringTourMessage(defaultDuringTourMessage);
    setDuringTourPreview(replacePlaceholders(defaultDuringTourMessage, initialVariables));

    setIsAfterTourMessageActive(false);
    setAfterTourMessage(defaultAfterTourMessage);
    setAfterTourPreview(replacePlaceholders(defaultAfterTourMessage, initialVariables));

    setIsScheduledTourMessageActive(false);
    setScheduledTourMessage(defaultScheduleTourMessage);
    setScheduledTourPreview(
      replacePlaceholders(
        defaultScheduleTourMessage,
        initialVariables,
      ),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [builderId]);

  useEffect(() => {
    if (!isInitialized && !isLoading && builderSettings) {
      if (builderSettings.during_tour_message) {
        setIsDuringTourMessageActive(true);
        setDuringTourMessage(builderSettings.during_tour_message);
        setDuringTourPreview(replacePlaceholders(builderSettings.during_tour_message, variables));
      }

      if (builderSettings.after_tour_message) {
        setIsAfterTourMessageActive(true);
        setAfterTourMessage(builderSettings.after_tour_message);
        setAfterTourPreview(replacePlaceholders(builderSettings.after_tour_message, variables));
      }

      if (builderSettings.scheduled_tour_message) {
        setIsScheduledTourMessageActive(true);
        setScheduledTourMessage(builderSettings.scheduled_tour_message);
        setScheduledTourPreview(
          replacePlaceholders(
            builderSettings.scheduled_tour_message,
            variables,
          ),
        );
      }

      setIsInitialized(true);
    }
  }, [builderSettings, variables, isLoading, isInitialized]);

  const handleMessageChange = ({
    target: { name, value },
  }: React.ChangeEvent<HTMLInputElement>) => {
    if (value.includes('@')) {
      const lastAtSymbolIndex = value.lastIndexOf('@');
      const searchText = value.slice(lastAtSymbolIndex + 1).toLowerCase();
      // const filteredVars = dynamicVariables.filter((variable) => variable.toLowerCase().includes(searchText));
      const filteredVars = Object.keys(variables).filter((key) => key.toLowerCase().includes(searchText));

      switch (name) {
        case 'duringTourMessage':
          setAnchorEl(duringTourInputRef.current);
          break;

        case 'afterTourMessage':
          setAnchorEl(afterTourInputRef.current);
          break;

        case 'scheduledTourMessage':
          setAnchorEl(scheduledTourInputRef.current);
          break;

        default:
          break;
      }

      setOpen(true);
      // setFilteredVariables(
      //   filteredVars.length > 0 ? filteredVars : dynamicVariables,
      // );
      setFilteredVariables(
        filteredVars.length > 0
          ? filteredVars.map((key) => `{${key}}`)
          : Object.keys(variables).map((key) => `{${key}}`),
      );
    }
    if (name === 'duringTourMessage') {
      const preview = replacePlaceholders(value, variables);
      setDuringTourMessage(value);
      setDuringTourPreview(preview);
    }

    if (name === 'afterTourMessage') {
      const preview = replacePlaceholders(value, variables);
      setAfterTourMessage(value);
      setAfterTourPreview(preview);
    }

    if (name === 'scheduledTourMessage') {
      const preview = replacePlaceholders(value, variables);
      setScheduledTourMessage(value);
      setScheduledTourPreview(preview);
    }
  };

  const handleVariableSelect = (
    variable: string,
    anchor: HTMLElement | null = null,
  ) => {
    const activeInput = anchor?.querySelector('textarea');
    const message = activeInput?.name === 'duringTourMessage'
      ? duringTourMessage
      : afterTourMessage;
    const lastAtSymbolIndex = message.lastIndexOf('@');
    const beforeCursor = message.slice(0, lastAtSymbolIndex);
    const afterCursor = message.slice(lastAtSymbolIndex).replace(/@/, '');
    const newMessage = `${beforeCursor}${variable}${afterCursor}`;

    if (activeInput?.name === 'duringTourMessage') {
      const preview = replacePlaceholders(newMessage, variables);
      setDuringTourMessage(newMessage);
      setDuringTourPreview(preview);
    }

    if (activeInput?.name === 'afterTourMessage') {
      const preview = replacePlaceholders(newMessage, variables);
      setAfterTourMessage(newMessage);
      setAfterTourPreview(preview);
    }

    if (activeInput?.name === 'scheduledTourMessage') {
      const preview = replacePlaceholders(newMessage, variables);
      setScheduledTourMessage(newMessage);
      setScheduledTourPreview(preview);
    }

    setOpen(false);

    requestAnimationFrame(() => {
      if (activeInput) {
        activeInput.focus();
      }
    });
  };

  const countMessageCharacters = (message: string) => {
    const cleanedMessage = message.replace(/\{(.*?)\}/g, (match, key) => (Object.prototype.hasOwnProperty.call(variables, key) ? '' : match));

    return cleanedMessage.length;
  };

  const handleChangeCommunity = (value: number, rest?: any) => {
    loadCommunityProperties(value);
    setSelectedCommunityId(value);
    setVariables({
      ...variables,
      community_name: rest.label,
      community_url: rest.url,
      community_sales_phone_number: rest.sales_agent,
    });
  };

  const handleChangeProperty = (value: number, rest?: any) => {
    setVariables({
      ...variables,
      property_address: rest?.address,
      property_subdivision: rest?.subdivision,
      property_marketing_name: rest?.marketing_name,
      property_city: rest?.city,
      property_state: rest?.state,
      property_lot_number: rest.lot_number,
      property_link: encodeURI(`${appConfig.WEBFLOW_URL}${builderName?.toLowerCase()}/property/${rest?.value}`),
    });
  };

  useEffect(() => {
    if (duringTourPreview) {
      setDuringTourPreview(replacePlaceholders(duringTourMessage, variables));
    }
    if (afterTourPreview) {
      setAfterTourPreview(replacePlaceholders(afterTourMessage, variables));
    }
    if (scheduledTourPreview) {
      setScheduledTourPreview(
        replacePlaceholders(scheduledTourMessage, variables),
      );
    }
    // eslint-disable-next-line
  }, [variables]);

  const handleSave = async () => {
    try {
      const during_tour_message = isDuringTourMessageActive
        ? duringTourMessage
        : null;
      const after_tour_message = isAfterTourMessageActive
        ? afterTourMessage
        : null;
      const scheduled_tour_message = isScheduledTourMessageActive
        ? scheduledTourMessage
        : null;
      const during_tour_message_interval = isDuringTourMessageActive
        ? duringTourMessageInterval
        : null;
      const after_tour_message_interval = isAfterTourMessageActive
        ? afterTourMessageInterval
        : null;

      saveMessaging({
        during_tour_message,
        after_tour_message,
        scheduled_tour_message,
        during_tour_message_interval,
        after_tour_message_interval,
      });
    } catch (errors) {
      console.log('errors:', errors);
    }
  };

  const closePopper = () => {
    if (open) {
      setOpen(false);
    }
  };

  const isSubmitEnabled = () => {
    const duringTourValidate = !isDuringTourMessageActive || (isDuringTourMessageActive && countMessageCharacters(duringTourMessage) < CHAR_LIMIT);
    const afterTourValidate = !isAfterTourMessageActive || (isAfterTourMessageActive && countMessageCharacters(afterTourMessage) < CHAR_LIMIT);
    const scheduledTourValidate = !isScheduledTourMessageActive
      || (isScheduledTourMessageActive
        && countMessageCharacters(scheduledTourMessage) < CHAR_LIMIT);
    return duringTourValidate && afterTourValidate && scheduledTourValidate;
  };

  const messageTime = (value: string): string => value === '1 min' ? '60 seconds' : `${value}utes`;

  useEffect(() => {
    if (!isDuringTourMessageActive) {
      setDuringTourMessageInterval(null);
      setDuringTourMessageTime('60 seconds');

      return;
    }

    setDuringTourMessageInterval(builderSettings?.during_tour_message_interval ?? '1 min');
    setDuringTourMessageTime(messageTime(builderSettings?.during_tour_message_interval ?? '1 min'));
  }, [isDuringTourMessageActive, builderSettings]);

  useEffect(() => {
    if (!isAfterTourMessageActive) {
      setAfterTourMessageInterval(null);
      setAfterTourMessageTime('30 minutes');

      return;
    }

    setAfterTourMessageInterval(builderSettings?.after_tour_message_interval ?? '30 min');
    setAfterTourMessageTime(messageTime(builderSettings?.after_tour_message_interval ?? '30 min'));
  }, [isAfterTourMessageActive, builderSettings]);


  return isLoading ? (
    <Grid container justify="center">
      <div className={classes.spinnerWrapper}>
        <Spinner />
      </div>
    </Grid>
  ) : (
    <>
      <Divider />
      <div className={classes.headerText}>
        <span>Follow Up Texts to Visitors</span>
      </div>
      <Grid container spacing={3}>
        <Grid item xs>
          <FormGroup style={{ gap: '30px' }}>
            <Popper
              style={{ width: '40vw' }}
              open={open}
              anchorEl={anchorEl}
              placement="bottom-start"
            >
              <Paper style={{ maxHeight: '400px', overflow: 'auto' }}>
                <MenuList>
                  {filteredVariables.map((variable) => (
                    <MenuItem
                      key={variable}
                      onMouseDown={(e) => {
                        e.stopPropagation();
                        handleVariableSelect(variable, anchorEl);
                      }}
                    >
                      {variable}
                    </MenuItem>
                  ))}
                </MenuList>
              </Paper>
            </Popper>
            <div className={classes.messageBox}>
              <div>
                <FormControlLabel
                  disabled={!canEdit}
                  label={(
                    <>
                      <span style={{ fontWeight: 'bold' }}>During Tour: </span>
                      <span className={classes.subtitle}>
                      triggers {duringTourMessageTime} after someone enters a code
                      into the lock
                      </span>
                    </>
                )}
                  control={(
                    <Switch
                      inputProps={{ style: { width: '40px' } }}
                      checked={isDuringTourMessageActive}
                      onChange={() => {
                        setIsDuringTourMessageActive(!isDuringTourMessageActive);
                      }}
                      name="duringTour"
                      disabled={!canEdit}
                    />
                  )}
                />
              </div>
              <Box className={classes.selectBox}>
                <Select
                  disabled={!isDuringTourMessageActive || !canEdit}
                  className={classes.select}
                  variant="outlined"
                  value={duringTourMessageInterval}
                  onChange={(
                    event: React.ChangeEvent<{ value: unknown }>,
                  ) => {
                    const { value } = event.target;

                    setDuringTourMessageInterval(value as string);
                    setDuringTourMessageTime(messageTime(value as string));
                  }}
                >
                  {intervalOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
              <div
                className={`${classes.textArea} ${!isDuringTourMessageActive
                && classes.disabled}`}
                style={{ marginBottom: '40px' }}
              >
                <TextField
                  ref={duringTourInputRef}
                  fullWidth
                  multiline
                  variant="outlined"
                  // className={`${classes.textArea} ${!isDuringTourMessageActive
                  //   && classes.disabled}`}
                  margin="normal"
                  id="duringTourMessage"
                  name="duringTourMessage"
                  value={duringTourMessage}
                  onChange={handleMessageChange}
                  onBlur={closePopper}
                  inputProps={{
                    style: {
                      padding: '0 12px',
                    },
                  }}
                  disabled={!canEdit}
                />
                <div
                  className={classes.textLimit}
                  style={{
                    color: countMessageCharacters(duringTourMessage) > CHAR_LIMIT ? 'red' : '#6B7280',
                  }}
                >
                  {countMessageCharacters(duringTourMessage)}
/
                  {CHAR_LIMIT}
                  {' '}
characters
                </div>
                <div className={classes.previewBox}>
                  <div className={classes.previewWithIcon}>
                    <img src={PreviewIcon} alt="Preview Icon" className={classes.previewIcon} />
                    <Typography className={classes.previewTitle}>Preview:</Typography>
                  </div>
                  <Typography>
                    {' '}
                    {duringTourPreview}
                    {' '}
                  </Typography>
                </div>
              </div>
            </div>

            <div className={classes.messageBox}>
              <div>
                <FormControlLabel
                  label={(
                    <>
                      <span style={{ fontWeight: 'bold' }}>After Tour: </span>
                      <span className={classes.subtitle}>
                        triggers {afterTourMessageTime} after they tour
                      </span>
                    </>
                  )}
                  disabled={!canEdit}
                  control={(
                    <Switch
                      checked={isAfterTourMessageActive}
                      onChange={() => {
                        setIsAfterTourMessageActive(!isAfterTourMessageActive);
                      }}
                      name="afterTour"
                      disabled={!canEdit}
                    />
                  )}
                />
              </div>
              <Box className={classes.selectBox}>
                <Select
                  disabled={!isAfterTourMessageActive || !canEdit}
                  className={classes.select}
                  variant="outlined"
                  value={afterTourMessageInterval}
                  onChange={(
                    event: React.ChangeEvent<{ value: unknown }>,
                  ) => {
                    const { value } = event.target;

                    setAfterTourMessageInterval(value as string);
                    setAfterTourMessageTime(messageTime(value as string));
                  }}
                >
                  {intervalOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
              <div className={`${classes.textArea} ${!isAfterTourMessageActive
              && classes.disabled}`}
              >
                <TextField
                  fullWidth
                  multiline
                  ref={afterTourInputRef}
                  variant="outlined"
                  margin="normal"
                  id="afterTourMessage"
                  name="afterTourMessage"
                  value={afterTourMessage}
                  onChange={handleMessageChange}
                  onBlur={closePopper}
                  inputProps={{
                    style: {
                      padding: '0 12px',
                    },
                  }}
                  disabled={!canEdit}
                />
                <div
                  className={classes.textLimit}
                  style={{
                    color: countMessageCharacters(afterTourMessage) > CHAR_LIMIT ? 'red' : '#6B7280',
                  }}
                >
                  {countMessageCharacters(afterTourMessage)}
/
                  {CHAR_LIMIT}
                  {' '}
characters
                </div>
                <div className={classes.previewBox}>
                  <div className={classes.previewWithIcon}>
                    <img src={PreviewIcon} alt="Preview Icon" className={classes.previewIcon} />
                    <Typography className={classes.previewTitle}>Preview:</Typography>
                  </div>
                  <Typography>
                    {' '}
                    {afterTourPreview}
                    {' '}
                  </Typography>
                </div>
              </div>
            </div>

            <div className={classes.messageBox}>
              <div>
                <FormControlLabel
                  label={(
                    <>
                      <span style={{ fontWeight: 'bold' }}>
                        Scheduled Tour:
                        {' '}
                      </span>
                      <span className={classes.subtitle}>
                        triggers 30 seconds after they schedule a tour
                      </span>
                    </>
                  )}
                  disabled={!canEdit}
                  control={(
                    <Switch
                      checked={isScheduledTourMessageActive}
                      onChange={() => {
                        setIsScheduledTourMessageActive(
                          !isScheduledTourMessageActive,
                        );
                      }}
                      name="scheduledTour"
                      disabled={!canEdit}
                    />
                  )}
                />
              </div>
              <div
                className={`${classes.textArea} ${
                  !isScheduledTourMessageActive && classes.disabled
                }`}
              >
                <TextField
                  fullWidth
                  multiline
                  ref={afterTourInputRef}
                  variant="outlined"
                  margin="normal"
                  id="scheduledTourMessage"
                  name="scheduledTourMessage"
                  value={scheduledTourMessage}
                  onChange={handleMessageChange}
                  onBlur={closePopper}
                  inputProps={{
                    style: {
                      padding: '0 12px',
                    },
                  }}
                  disabled
                />
                <div
                  className={classes.textLimit}
                  style={{
                    color:
                      countMessageCharacters(scheduledTourMessage) > CHAR_LIMIT
                        ? 'red'
                        : '#6B7280',
                  }}
                >
                  {countMessageCharacters(scheduledTourMessage)}
/
                  {CHAR_LIMIT}
                  {' '}
                  characters
                </div>
                <div className={classes.previewBox}>
                  <div className={classes.previewWithIcon}>
                    <img
                      src={PreviewIcon}
                      alt="Preview Icon"
                      className={classes.previewIcon}
                    />
                    <Typography className={classes.previewTitle}>
                      Preview:
                    </Typography>
                  </div>
                  <Typography>
                    {' '}
                    {scheduledTourPreview}
                    {' '}
                  </Typography>
                </div>
                <Typography variant="caption" style={{ color: '#6B7280', marginTop: '8px', display: 'block' }}>
                  To include the scheduled tour's date or time, make sure to use the variables
                  {' '}
                  {'{scheduled_tour_date}'}
                  {' '}
and/or
                  {' '}
                  {'{scheduled_tour_time}'}
                  {' '}
in your message.
                </Typography>
              </div>
            </div>
          </FormGroup>
        </Grid>
        <Grid item xs>
          <Table className={classes.dropdownTable} aria-label="customized table">
            <TableBody>
              <TableRow className={classes.tableRow}>
                <TableCell align="left" className={classes.tableCell}>
                  <h3 style={{ marginBottom: '20px' }}>Select Community Preview</h3>
                  <CommunitySearch
                    initialValue={selectedCommunityId}
                    handleChange={handleChangeCommunity}
                    builderId={builderId}
                  />
                  <h3 style={{ marginBottom: '20px', marginTop: '10px' }}>Select Property Preview</h3>
                  <PropertySearch
                    handleChange={handleChangeProperty}
                  />
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
          <Table className={classes.table} aria-label="customized table" style={{ height: isLg ? '100vh' : '100%' }}>
            <TableHead className={classes.tableHead}>
              <TableRow>
                <TableCell style={{ width: '200px' }}>
                  Dynamic Variables
                </TableCell>
                <TableCell className={classes.tableCell} />
              </TableRow>
            </TableHead>
            <TableBody>
              {Object.entries(groupHeaders).map(([groupName, keys]) => {
                if (groupName === 'Scheduled Tour Variables') {
                  return null;
                }

                return (
                  <React.Fragment key={groupName}>
                    <TableRow className={classes.tableRow}>
                      <TableCell
                        colSpan={2}
                        align="left"
                        className={classes.tableCell}
                        style={{
                          fontWeight: 'bold',
                          backgroundColor: '#f5f5f5',
                        }}
                      >
                        {groupName}
                      </TableCell>
                    </TableRow>
                    {keys.map((key) => {
                      const variableKey = key as keyof Variables;

                      return (
                        <TableRow className={classes.tableRow} key={key}>
                          <TableCell align="left" className={classes.tableCell}>
                            {`{${key}}`}
                          </TableCell>
                          <TableCell align="left" className={classes.tableCell}>
                            {variables[variableKey]}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </Grid>
      </Grid>
      {/* <Divider style={{ margin: '20px 0 20px' }} /> */}
      <div className={classes.navButtons}>
        <CustomButton
          className={classes.submitButtons}
          variant="orange"
          onClick={() => handleSave()}
          disabled={!isSubmitEnabled() || !canEdit}
        >
          Submit
        </CustomButton>
      </div>
    </>
  );
};

export default React.memo(Messaging);
